/**
 * SimpleCycleConnector — Renders cycle decorations on an SVG overlay pane.
 *
 * Expanded groups: every cycle is drawn as a rounded boundary that wraps only
 * the cells belonging to that cycle, carrying the "↻ ×N" iteration badge. The
 * rectangle is derived from the live cell states (so it follows zoom, pan and
 * node moves) and is clamped by the caller to the parent group's inner content
 * area, so it can never render outside the group.
 *
 * Collapsed groups: a compact "↻N×M" chip is drawn on the swimlane header.
 */

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CycleBoundaryDef {
  id: string;
  label: string; // e.g., "↻ ×3"
  color: string;
  /** Uniformly padded, group-clamped rectangle in screen coordinates. */
  bounds: BoundingBox;
  // Zoom scale factor for scale-aware offsets
  scale: number;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

function badgeHeightFor(scale: number): number {
  return Math.max(8, 9 * scale) + 5;
}

/**
 * How far the iteration badge rises above the boundary's top edge.
 * Callers reserve this when sizing the boundary (see `computeCycleBoundaryBounds`).
 */
export function cycleBadgeOverhang(scale: number): number {
  return badgeHeightFor(scale) / 2;
}

/**
 * Size the boundary around a cycle's member cells.
 *
 * The padding is applied **uniformly** on all four sides and shrinks to the
 * tightest side when the group is close, so the nodes always sit evenly inside
 * the frame instead of one side hugging a node while another is cut off.
 *
 * `topOverhang` reserves room above the frame for the iteration badge, which is
 * centred on the top edge — without it the badge could ride up into the group's
 * header. The result is finally intersected with the group so the boundary can
 * never extend past it.
 *
 * Returns null when nothing is left to draw.
 */
export function computeCycleBoundaryBounds(
  memberBounds: BoundingBox,
  desiredPadding: number,
  topOverhang: number,
  groupInnerBounds: BoundingBox | null,
): BoundingBox | null {
  const padding = groupInnerBounds
    ? Math.max(
        0,
        Math.min(
          desiredPadding,
          availableClearance(memberBounds, groupInnerBounds, topOverhang),
        ),
      )
    : desiredPadding;

  const padded: BoundingBox = {
    x: memberBounds.x - padding,
    y: memberBounds.y - padding,
    width: memberBounds.width + padding * 2,
    height: memberBounds.height + padding * 2,
  };

  return intersect(padded, groupInnerBounds);
}

/**
 * Smallest gap between the member rectangle and any edge of its group, with the
 * badge overhang discounted from the top.
 * Negative when a member already sits outside the group's content area.
 */
function availableClearance(
  memberBounds: BoundingBox,
  groupInnerBounds: BoundingBox,
  topOverhang: number,
): number {
  return Math.min(
    memberBounds.x - groupInnerBounds.x,
    groupInnerBounds.x + groupInnerBounds.width - (memberBounds.x + memberBounds.width),
    memberBounds.y - groupInnerBounds.y - topOverhang,
    groupInnerBounds.y + groupInnerBounds.height - (memberBounds.y + memberBounds.height),
  );
}

function intersect(
  bounds: BoundingBox,
  limit: BoundingBox | null,
): BoundingBox | null {
  if (!limit) return bounds;

  const x = Math.max(bounds.x, limit.x);
  const y = Math.max(bounds.y, limit.y);
  const right = Math.min(bounds.x + bounds.width, limit.x + limit.width);
  const bottom = Math.min(bounds.y + bounds.height, limit.y + limit.height);

  if (right <= x || bottom <= y) {
    return null;
  }

  return { x, y, width: right - x, height: bottom - y };
}

/**
 * Render the boundary that visually groups the cells belonging to one cycle.
 *
 * Drawn as an SVG `<g>` **inside maxGraph's own pane**, using the same absolute
 * view coordinates the cell shapes use. That is what keeps it locked to the
 * nodes: a separately positioned HTML overlay has its own origin and drifts
 * from the canvas whenever the two disagree.
 *
 * `def.bounds` arrives uniformly padded around the member cells and clamped to
 * the parent group, so everything drawn here stays inside the group. The
 * iteration badge is centred on the top edge, clear of the nodes and of the
 * normal node-to-node connections.
 */
export function renderCycleBoundary(def: CycleBoundaryDef): SVGElement {
  const { id, label, color, bounds, scale } = def;

  const strokeWidth = Math.max(1, 2 * scale);

  const g = document.createElementNS(SVG_NS, 'g');
  g.setAttribute('data-cycle-id', id);
  g.setAttribute('class', 'cycle-boundary');
  g.setAttribute('pointer-events', 'none');

  const radius = Math.min(12 * scale, bounds.width / 2, bounds.height / 2);

  const rect = document.createElementNS(SVG_NS, 'rect');
  rect.setAttribute('x', `${bounds.x}`);
  rect.setAttribute('y', `${bounds.y}`);
  rect.setAttribute('width', `${bounds.width}`);
  rect.setAttribute('height', `${bounds.height}`);
  rect.setAttribute('rx', `${radius}`);
  rect.setAttribute('ry', `${radius}`);
  rect.setAttribute('fill', color);
  rect.setAttribute('fill-opacity', '0.06');
  rect.setAttribute('stroke', color);
  rect.setAttribute('stroke-width', `${strokeWidth}`);
  rect.setAttribute('stroke-dasharray', `${6 * scale},${4 * scale}`);
  rect.setAttribute('stroke-linecap', 'round');
  rect.setAttribute('stroke-linejoin', 'round');

  g.appendChild(rect);

  const badge = buildIterationBadge(label, color, bounds, scale);
  if (badge) {
    g.appendChild(badge);
  }

  return g;
}

/**
 * "↻ ×N" pill centred on the boundary's top edge, inset from the right corner.
 *
 * Sitting on the frame line keeps it visible at any padding size while only
 * ever overlapping the boundary stroke itself — never a node, a node label, or
 * a node-to-node connection.
 */
function buildIterationBadge(
  label: string,
  color: string,
  bounds: BoundingBox,
  scale: number,
): SVGElement | null {
  const fontSize = Math.max(8, 9 * scale);
  const badgeHeight = badgeHeightFor(scale);
  const badgeWidth = label.length * fontSize * 0.62 + fontSize;

  // Nothing sensible to draw if the frame is narrower than the badge itself.
  if (bounds.width < badgeWidth) {
    return null;
  }

  const cornerInset = Math.min(12 * scale, Math.max(0, bounds.width - badgeWidth));
  const badgeX = bounds.x + bounds.width - cornerInset - badgeWidth;
  const badgeY = bounds.y - badgeHeight / 2;

  const group = document.createElementNS(SVG_NS, 'g');
  group.setAttribute('class', 'cycle-boundary-badge');

  const pill = document.createElementNS(SVG_NS, 'rect');
  pill.setAttribute('x', `${badgeX}`);
  pill.setAttribute('y', `${badgeY}`);
  pill.setAttribute('width', `${badgeWidth}`);
  pill.setAttribute('height', `${badgeHeight}`);
  pill.setAttribute('rx', `${Math.min(4 * scale, badgeHeight / 2)}`);
  pill.setAttribute('fill', color);
  pill.setAttribute('fill-opacity', '0.9');

  const text = document.createElementNS(SVG_NS, 'text');
  text.setAttribute('x', `${badgeX + badgeWidth / 2}`);
  text.setAttribute('y', `${badgeY + badgeHeight / 2}`);
  text.setAttribute('text-anchor', 'middle');
  text.setAttribute('dominant-baseline', 'central');
  text.setAttribute('font-family', 'Inter, system-ui, sans-serif');
  text.setAttribute('font-size', `${fontSize}`);
  text.setAttribute('font-weight', '600');
  text.setAttribute('fill', '#ffffff');
  text.textContent = label;

  group.appendChild(pill);
  group.appendChild(text);

  return group;
}

/**
 * Clear all cycle boundaries and chips from maxGraph's overlay pane.
 */
export function clearAllConnectors(pane: SVGElement | HTMLElement): void {
  const decorations = pane.querySelectorAll('.cycle-boundary, .cycle-chip');
  decorations.forEach((c) => c.remove());
}

/**
 * Interface for collapsed group cycle chip.
 */
export interface CollapsedCycleChipDef {
  groupId: string;
  groupX: number;
  groupY: number;
  groupW: number;
  scale: number;
  cycles: Array<{ label: string; color: string }>; // e.g., [{ label: "↻ ×3", color: "#6366F1" }]
}

/**
 * Render a compact chip for cycles in a collapsed group, on the swimlane header.
 * Uses absolute view coordinates, like `renderCycleBoundary`.
 */
export function renderCollapsedCycleChip(def: CollapsedCycleChipDef): SVGElement {
  const g = document.createElementNS(SVG_NS, 'g');

  const scale = def.scale;
  const chipWidth = 60 * scale;
  const chipHeight = 20 * scale;
  const chipX = def.groupX + def.groupW - chipWidth - 10 * scale;
  const chipY = def.groupY + 5 * scale;

  g.setAttribute('data-group-id', def.groupId);
  g.setAttribute('class', 'cycle-chip');
  g.setAttribute('pointer-events', 'none');

  const rect = document.createElementNS(SVG_NS, 'rect');
  rect.setAttribute('x', `${chipX}`);
  rect.setAttribute('y', `${chipY}`);
  rect.setAttribute('width', `${chipWidth}`);
  rect.setAttribute('height', `${chipHeight}`);
  rect.setAttribute('fill', '#f8f9fa');
  rect.setAttribute('stroke', '#dee2e6');
  rect.setAttribute('stroke-width', `${scale}`);
  rect.setAttribute('rx', `${4 * scale}`);

  g.appendChild(rect);

  // Create a compact label showing all cycles
  const cycleCount = def.cycles.length;
  const totalIterations = def.cycles.reduce((sum, c) => {
    const match = c.label.match(/×(\d+)/);
    return sum + (match ? parseInt(match[1], 10) : 0);
  }, 0);

  const label = `${cycleCount}×${totalIterations}`;

  const text = document.createElementNS(SVG_NS, 'text');
  text.setAttribute('x', `${chipX + chipWidth / 2}`);
  text.setAttribute('y', `${chipY + chipHeight / 2}`);
  text.setAttribute('text-anchor', 'middle');
  text.setAttribute('dominant-baseline', 'central');
  text.setAttribute('font-family', 'Inter, system-ui, sans-serif');
  text.setAttribute('font-size', `${10 * scale}`);
  text.setAttribute('font-weight', 'bold');
  text.setAttribute('fill', '#495057');
  text.textContent = `↻${label}`;

  g.appendChild(text);

  return g;
}
